import classnames from 'classnames';
import React, { useMemo } from 'react';

import { Maybe } from 'types/graphql-api.generated';

import QuickInfo from 'common/components/ShowtimeModal/QuickInfo';
import TheaterAmenities from 'common/components/ShowtimeModal/TheaterAmenities';
import { PartialShowtime } from 'common/components/ShowtimeModal/types';
import ShowtimeExperiences from 'common/components/Showtimes/ShowtimeExperiences';
import format, { HOURS_FORMATS } from 'common/tools/date/format';
import getVersionLabel, {
  getVersionGroupKey
} from 'common/tools/showtime/getVersionLabel';
import trans from 'common/tools/translations/trans';

import styles from './styles.module.scss';

type QuickInformationsProps = {
  date?: Date;
  experiences: string[];
  movieRuntime: string | null;
  showtime?: Maybe<PartialShowtime>;
};

const aboutEndLabel = trans('showtimes.modal.about_end');

const getRuntimeHours = (
  date: Date | undefined,
  movieRuntime: string | null
) => {
  if (!date || !movieRuntime) {
    return null;
  }

  const runtimeMatch = movieRuntime.match(/(\d+)h\s*(\d+)min/u);
  const runtimeHours = runtimeMatch ? runtimeMatch[1] ?? '0' : '0';
  const runtimeMinutes = runtimeMatch ? runtimeMatch[2] ?? '0' : '0';

  const runtimeDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDay(),
    date.getHours() + parseInt(runtimeHours, 10),
    date.getMinutes() + parseInt(runtimeMinutes, 10) + 15
  );
  return format(runtimeDate, HOURS_FORMATS.SHORT);
};

const QuickInformations = ({
  date,
  movieRuntime,
  showtime
}: QuickInformationsProps) => {
  const runtimeDateString = getRuntimeHours(date, movieRuntime);

  const versionLabel = useMemo(() => {
    if (!showtime?.diffusionVersion) {
      return undefined;
    }

    const key = getVersionGroupKey(
      showtime.diffusionVersion,
      showtime.tags ?? []
    );

    return getVersionLabel(key);
  }, [showtime]);

  return (
    <>
      {date && (
        <div className={styles.startTime}>
          {format(date, HOURS_FORMATS.SHORT)}
        </div>
      )}
      <div className={styles.quickInfoBanner}>
        {runtimeDateString && (
          <QuickInfo info={`${aboutEndLabel} ${runtimeDateString}`} />
        )}
        <QuickInfo info={versionLabel} />
        <ShowtimeExperiences
          experiences={showtime?.tags}
          extraClassName={classnames(styles.quickInfo, styles.experience)}
        />
        <TheaterAmenities
          amenities={showtime?.service ?? []}
          extraClassName={styles.quickInfo}
          showtime={showtime}
        />
      </div>
    </>
  );
};

export default QuickInformations;
